import {createFragmentContainer, graphql} from 'react-relay';
import {sortBreakdownDeliverables} from '../ProjectPortfolioReportUtils';
import {useIntl} from 'react-intl';
import {DELIVERABLE_BREAKDOWN_TYPE} from '../../../../constants';

const MonthByMonthDeliverableLoader = ({relay, viewer, children, ...props}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const getAggregatedDeliverableFinancialBreakdown = () => {
		const currency = viewer.company.currency;
		const aggregatedDeliverableFinancialBreakdown = viewer.project.aggregatedDeliverableFinancialBreakdown.map(
			aggregatedDeliverableFinancialBreakdown => {
				const data = {...aggregatedDeliverableFinancialBreakdown, currency};
				if (!data.deliverable && data.breakdownType === DELIVERABLE_BREAKDOWN_TYPE.DELIVERABLE) {
					data.deliverable = {
						name: formatMessage({id: 'project_portfolio_report.deleted_deliverable'}),
					};
				}
				return data;
			}
		);

		sortBreakdownDeliverables(aggregatedDeliverableFinancialBreakdown);
		return aggregatedDeliverableFinancialBreakdown;
	};

	const breakdownKey = deliverable =>
		deliverable.breakdownType === DELIVERABLE_BREAKDOWN_TYPE.DELIVERABLE
			? `${deliverable.breakdownType}-${deliverable.deliverable?.id}`
			: deliverable.breakdownType;

	const currency = viewer.company.currency;
	const timeAggregatedFinancialNumbers = viewer.project.timeAggregatedFinancialNumbers.map(row => ({...row, currency}));
	const expenseAggregatedFinancialNumbers = viewer.project.expenseAggregatedFinancialNumbers.map(row => ({...row, currency}));
	sortBreakdownDeliverables(timeAggregatedFinancialNumbers);
	sortBreakdownDeliverables(expenseAggregatedFinancialNumbers);

	const timeBreakdownKeys = timeAggregatedFinancialNumbers.map(breakdownKey);
	const expenseBreakdownKeys = expenseAggregatedFinancialNumbers.map(breakdownKey);

	const aggregatedDeliverableFinancialBreakdown = getAggregatedDeliverableFinancialBreakdown();
	const breakdownKeys = aggregatedDeliverableFinancialBreakdown.map(breakdownKey);

	let year;
	if (breakdownKeys.length > 0) {
		year = aggregatedDeliverableFinancialBreakdown[0].aggregatedFinancialNumbers[0].split(',')[0].split('-')[0];
	}

	for (let i = 0; i < breakdownKeys.length; i++) {
		if (timeBreakdownKeys.includes(breakdownKeys[i])) {
			aggregatedDeliverableFinancialBreakdown[i].timeAggregatedFinancialNumbers =
				timeAggregatedFinancialNumbers[timeBreakdownKeys.indexOf(breakdownKeys[i])].timeAggregatedFinancialNumbers;
		} else {
			aggregatedDeliverableFinancialBreakdown[i].timeAggregatedFinancialNumbers = [`${year}-01,${year}-12,0`];
		}

		if (expenseBreakdownKeys.includes(breakdownKeys[i])) {
			aggregatedDeliverableFinancialBreakdown[i].expenseAggregatedFinancialNumbers =
				expenseAggregatedFinancialNumbers[
					expenseBreakdownKeys.indexOf(breakdownKeys[i])
				].expenseAggregatedFinancialNumbers;
		} else {
			aggregatedDeliverableFinancialBreakdown[i].expenseAggregatedFinancialNumbers = [`${year}-01,${year}-12,0`];
		}
	}

	return children({data: {rows: aggregatedDeliverableFinancialBreakdown}, ...props});
};

export const monthByMonthDeliverableLoaderQuery = graphql`
	query MonthByMonthDeliverableLoader_Query(
		$timeSearchQuery: TaskSearchQueryType!
		$expenseSearchQuery: TaskSearchQueryType!
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$projectId: ID!
		$selectedSingleValue: FinancialAggregatedNumber!
		$ignoreBaselineWinChance: Boolean
		$excludeOpportunityActuals: Boolean
	) {
		viewer {
			actualPersonId
			component(name: "project_portfolio_month_by_month_deliverable_loader")
			...MonthByMonthDeliverableLoader_viewer
				@arguments(
					timeSearchQuery: $timeSearchQuery
					expenseSearchQuery: $expenseSearchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					projectId: $projectId
					selectedSingleValue: $selectedSingleValue
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				)
		}
	}
`;

export default createFragmentContainer(MonthByMonthDeliverableLoader, {
	viewer: graphql`
		fragment MonthByMonthDeliverableLoader_viewer on Viewer
		@argumentDefinitions(
			timeSearchQuery: {type: "TaskSearchQueryType!"}
			expenseSearchQuery: {type: "TaskSearchQueryType!"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
			projectId: {type: "ID!"}
			selectedSingleValue: {type: "FinancialAggregatedNumber!"}
			ignoreBaselineWinChance: {type: "Boolean"}
			excludeOpportunityActuals: {type: "Boolean"}
		) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				aggregatedDeliverableFinancialBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: MONTH
					aggregates: [$selectedSingleValue]
					filterRowsWithNoValues: true
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				) {
					breakdownType
					deliverable {
						id
						name
					}
					aggregatedFinancialNumbers
				}
				timeAggregatedFinancialNumbers: aggregatedDeliverableFinancialBreakdown(
					searchQuery: $timeSearchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: MONTH
					aggregates: [$selectedSingleValue]
					filterRowsWithNoValues: true
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				) {
					breakdownType
					deliverable {
						id
						name
					}
					timeAggregatedFinancialNumbers: aggregatedFinancialNumbers
				}
				expenseAggregatedFinancialNumbers: aggregatedDeliverableFinancialBreakdown(
					searchQuery: $expenseSearchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: MONTH
					aggregates: [$selectedSingleValue]
					filterRowsWithNoValues: true
					ignoreBaselineWinChance: $ignoreBaselineWinChance
					excludeOpportunityActuals: $excludeOpportunityActuals
				) {
					breakdownType
					deliverable {
						id
						name
					}
					expenseAggregatedFinancialNumbers: aggregatedFinancialNumbers
				}
			}
		}
	`,
});
